import type { JSX } from "react";

import {
  Field,
  FieldContent,
  FieldError,
  FieldLabel,
} from "@frontend/components/atoms/Field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@frontend/components/atoms/Select";
import { useField } from "@frontend/hooks/form";
import type { NumberField } from "@frontend/lib/form";

export type ReferenceFieldItem = {
  id: number;
  label: string;
  available?: boolean | null;
};

export type ReferenceFieldProps = {
  className?: string;
  label: string;
  placeholder?: string;
  items: ReferenceFieldItem[];
  field: NumberField<number | null | undefined>;
  disabled?: boolean;
  onValueChange?: (value: number | null) => void;
};

export function ReferenceField({
  className,
  label,
  placeholder,
  items,
  field,
  disabled,
  onValueChange,
}: ReferenceFieldProps): JSX.Element {
  const { value, setValue, error } = useField(field);

  return (
    <Field className={className} invalid={Boolean(error)}>
      <FieldLabel>{label}</FieldLabel>

      <FieldContent>
        <Select
          items={items.map((item) => ({
            label: item.label,
            value: String(item.id),
          }))}
          value={null == value ? null : String(value)}
          disabled={disabled}
          onValueChange={(next) => {
            const id = null === next ? null : Number(next);

            setValue(id);
            onValueChange?.(id);
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>

          <SelectContent>
            {items.map((item) => (
              <SelectItem
                key={item.id}
                value={String(item.id)}
                disabled={false === item.available && item.id !== value}
              >
                {item.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <FieldError match={Boolean(error)}>{error}</FieldError>
      </FieldContent>
    </Field>
  );
}
